import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Star, ShieldCheck, Clock, MapPin, ArrowLeft, Heart, Share2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useFlags } from "@/hooks/useFlags";
import { useFavorites } from "@/hooks/useFavorites";
import { Button } from "@/components/ui/button";
import { ActivityBookingDialog } from "@/components/ActivityBookingDialog";

export default function ActivityDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const v4 = useFlags().v4_enabled;
  const { toggle, isFav } = useFavorites();
  const [a, setA] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [hero, setHero] = useState("");
  const [open, setOpen] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [lbIdx, setLbIdx] = useState(-1);

  useEffect(() => {
    api.get(`/activities/${id}`).then((r) => {
      setA(r.data.activity); setReviews(r.data.reviews);
      setHero(r.data.activity.image);
    }).catch(() => setA(false));
  }, [id]);

  useEffect(() => {
    if (a && a.category) api.get("/activities", { params: { category: a.category } })
      .then((r) => setSimilar(r.data.activities.filter((x) => x.id !== a.id).slice(0, 4))).catch(() => {});
  }, [a]);

  const gallery = (a?.images && a.images.length ? a.images : [a?.image]).filter(Boolean);

  useEffect(() => {
    if (lbIdx < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLbIdx(-1);
      else if (e.key === "ArrowLeft") setLbIdx((i) => (i - 1 + gallery.length) % gallery.length);
      else if (e.key === "ArrowRight") setLbIdx((i) => (i + 1) % gallery.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lbIdx, gallery.length]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: a.name, url });
      else { await navigator.clipboard.writeText(url); toast.success(t("activities.linkCopied")); }
    } catch (e) { /* user cancelled the share sheet */ console.debug("share cancelled", e); }
  };

  if (a === false) return <div className="section-sand min-h-[60vh] flex items-center justify-center"><p className="text-muted2">{t("activities.empty")}</p></div>;
  if (!a) return <div className="section-sand min-h-[60vh]" />;

  return (
    <div className="section-sand min-h-[80vh] py-10 px-4" data-testid="activity-detail-page">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => nav("/activites")} className="flex items-center gap-1 text-sm text-canard font-semibold mb-4"><ArrowLeft className="w-4 h-4" /> {t("activities.title")}</button>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <button onClick={() => setLbIdx(Math.max(0, gallery.indexOf(hero)))} data-testid="detail-hero-btn" className="relative block w-full rounded-2xl overflow-hidden cursor-zoom-in">
              <img src={fileUrl(hero)} alt={a.name} className="w-full h-72 object-cover" data-testid="detail-hero" />
              {a.partner_id && <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[11px] font-bold text-emeraude-dark bg-white/90 px-2.5 py-1 rounded-full shadow-sm"><ShieldCheck className="w-3.5 h-3.5" /> {t("activities.verified")}</span>}
            </button>
            {gallery.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto" data-testid="detail-gallery">
                {gallery.map((g, i) => (
                  <button key={g} onClick={() => setHero(g)} className={`shrink-0 rounded-lg overflow-hidden border-2 ${hero === g ? "border-canard" : "border-transparent"}`}>
                    <img src={fileUrl(g)} alt="" className="w-20 h-16 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading font-extrabold text-3xl text-ink">{a.name}</h1>
              {a.rating > 0 && <span className="flex items-center gap-1 text-sm font-bold text-canard-dark"><Star className="w-4 h-4 fill-emeraude text-emeraude" /> {a.rating} ({a.reviews_count})</span>}
              <button data-testid="detail-fav" onClick={() => toggle(a.id)} aria-label="favorite" className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-pastel/40 transition-colors">
                <Heart className={`w-5 h-5 ${isFav(a.id) ? "fill-red-500 text-red-500" : "text-muted2"}`} />
              </button>
              <button data-testid="detail-share" onClick={share} aria-label="share" className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-pastel/40 transition-colors">
                <Share2 className="w-5 h-5 text-canard" />
              </button>
            </div>
            {a.bookings_count > 0 && <p data-testid="detail-social-proof" className="text-sm font-semibold text-emeraude-dark mt-1">🔥 {t("activities.alreadyBooked", { n: a.bookings_count })}</p>}
            <p className="text-muted2 font-body mt-3">{a.description}</p>
            <div className="space-y-2 mt-4 text-sm font-body">
              {a.duration && <p className="flex items-center gap-2 text-ink"><Clock className="w-4 h-4 text-canard" /> {a.duration}</p>}
              {a.schedule && <p className="flex items-center gap-2 text-ink"><Clock className="w-4 h-4 text-canard" /> {a.schedule}</p>}
              {a.meeting_point && <p className="flex items-center gap-2 text-ink"><MapPin className="w-4 h-4 text-canard" /> {a.meeting_point}</p>}
            </div>
            {a.meeting_point && (
              <div className="mt-4 rounded-2xl overflow-hidden border border-border" data-testid="detail-map">
                <iframe title="meeting-point-map" width="100%" height="200" loading="lazy" style={{ border: 0 }}
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(`${a.meeting_point} ${a.country || ""}`)}&output=embed`} />
              </div>
            )}
            <div className="flex items-center justify-between mt-6 bg-white rounded-2xl border border-border p-4">
              <span className="text-lg font-bold text-canard">{t("activities.from")} {a.price_from}€</span>
              <Button data-testid="detail-book-btn" onClick={() => setOpen(true)} className="rounded-full bg-canard hover:bg-canard-dark font-bold h-11 px-6">{v4 ? t("activities.book") : t("activities.request")}</Button>
            </div>
          </div>
        </div>

        <div className="mt-10 max-w-2xl">
          <h2 className="font-heading font-bold text-xl text-ink mb-3">{t("activities.reviews")}</h2>
          {reviews.length === 0 ? <p className="text-muted2 font-body">{t("activities.noReviews")}</p> : (
            <div className="space-y-3">
              {reviews.map((r) => (
                <div key={r.id} className="bg-white rounded-2xl border border-border p-4">
                  <div className="flex items-center gap-2"><span className="font-bold text-ink">{r.author_name}</span><span className="flex gap-0.5">{[...Array(5)].map((_, s) => <Star key={s} className={`w-3.5 h-3.5 ${s < r.rating ? "fill-emeraude text-emeraude" : "text-border"}`} />)}</span></div>
                  {r.comment && <p className="text-muted2 text-sm mt-1">{r.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        {similar.length > 0 && (
          <div className="mt-10" data-testid="detail-similar">
            <h2 className="font-heading font-bold text-xl text-ink mb-4">{t("activities.similar")}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {similar.map((s) => (
                <button key={s.id} data-testid={`similar-${s.id}`} onClick={() => nav(`/activites/${s.id}`)} className="text-left bg-white rounded-2xl border border-border overflow-hidden shadow-card hover:shadow-lg transition-shadow">
                  <img src={fileUrl(s.image)} alt={s.name} className="w-full h-28 object-cover" />
                  <div className="p-3">
                    <p className="font-heading font-bold text-sm text-ink line-clamp-1">{s.name}</p>
                    <span className="text-xs font-bold text-canard">{t("activities.from")} {s.price_from}€</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <ActivityBookingDialog activity={a} open={open} onOpenChange={setOpen} v4={v4} testidPrefix="det" />
      {lbIdx >= 0 && (
        <div data-testid="detail-lightbox" onClick={() => setLbIdx(-1)} className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4">
          <button onClick={() => setLbIdx(-1)} aria-label="close" className="absolute top-4 right-4 text-white"><X className="w-8 h-8" /></button>
          {gallery.length > 1 && <button data-testid="lb-prev" onClick={(e) => { e.stopPropagation(); setLbIdx((i) => (i - 1 + gallery.length) % gallery.length); }} aria-label="prev" className="absolute left-3 sm:left-6 text-white"><ChevronLeft className="w-10 h-10" /></button>}
          <img src={fileUrl(gallery[lbIdx])} alt={a.name} onClick={(e) => e.stopPropagation()} className="max-h-[90vh] max-w-[95vw] object-contain rounded-lg" />
          {gallery.length > 1 && <button data-testid="lb-next" onClick={(e) => { e.stopPropagation(); setLbIdx((i) => (i + 1) % gallery.length); }} aria-label="next" className="absolute right-3 sm:right-6 text-white"><ChevronRight className="w-10 h-10" /></button>}
        </div>
      )}
    </div>
  );
}
